import React, { useEffect, useRef, useState } from 'react';
import type { Language } from '../../types';
import { getTranslation } from '../../utils/translations';
import { parseDateTimeLocalValue, toDateTimeLocalValue, isDateTimeLocalInRange } from '../../utils/intermittentFasting';
import XIcon from '../icons/XIcon';
import CrescentMoonIcon from '../icons/CrescentMoonIcon';
import ChartBarIcon from '../icons/ChartBarIcon';
import ConfirmationModal from '../ConfirmationModal';
import FastingSmartRing from './FastingSmartRing';
import FastingDateTimeField from './FastingDateTimeField';

interface FastingTrackerModalProps {
    isOpen: boolean;
    onClose: () => void;
    
    isActive: boolean;
    isScheduled?: boolean;
    goalHours: number;
    language?: Language;
    isBusy?: boolean;
    
    // Active State details
    remainingMs?: number;
    endTimestamp?: number | null;
    startedAt?: number | null;
    
    // Callbacks
    onStart: () => void | Promise<void>;
    onEndFast: () => void | Promise<void>;
    onSchedule?: (scheduledStartAt: Date) => void | Promise<void>;
    onCancelSchedule?: () => void | Promise<void>;
    onGoalChange: (hours: number) => void;
    onUpdateStartTime?: (newStartedAt: Date) => void | Promise<void>;
    onOpenStatistics: () => void;
}

const FastingTrackerModal: React.FC<FastingTrackerModalProps> = ({
    isOpen,
    onClose,
    isActive,
    isScheduled = false,
    goalHours,
    language,
    isBusy = false,
    remainingMs = 0,
    endTimestamp,
    startedAt,
    scheduledStartAt,
    onStart,
    onEndFast,
    onSchedule,
    onCancelSchedule,
    onGoalChange,
    onUpdateStartTime,
    onOpenStatistics,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const dialogRef = useRef<HTMLDivElement>(null);

    const [showStartConfirm, setShowStartConfirm] = useState(false);
    const [showActiveMenu, setShowActiveMenu] = useState(false);
    const [showSetupMenu, setShowSetupMenu] = useState(false);
    const [showScheduledMenu, setShowScheduledMenu] = useState(false);
    const [showStartTimeEditor, setShowStartTimeEditor] = useState(false);
    const [startTimeEditorMode, setStartTimeEditorMode] = useState<'future' | 'past' | 'edit-active' | 'edit-scheduled'>('future');
    const [startTimeValue, setStartTimeValue] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setShowStartConfirm(false);
            setShowActiveMenu(false);
            setShowSetupMenu(false);
            setShowScheduledMenu(false);
            setShowStartTimeEditor(false);
            setStartTimeEditorMode('future');
        }
    }, [isOpen]);

    const openStartTimeEditor = (mode: typeof startTimeEditorMode) => {
        const now = new Date();
        now.setSeconds(0, 0);
        if (mode === 'future') {
            const defaultSchedule = new Date(now.getTime() + 60 * 60 * 1000);
            setStartTimeValue(toDateTimeLocalValue(defaultSchedule));
        } else if (mode === 'past') {
            setStartTimeValue(toDateTimeLocalValue(now));
        } else if (startedAt != null) {
            setStartTimeValue(toDateTimeLocalValue(new Date(startedAt)));
        }
        setStartTimeEditorMode(mode);
        setShowStartTimeEditor(true);
    };

    useEffect(() => {
        if (isOpen && startedAt != null && isActive && !showStartTimeEditor) {
            setStartTimeValue(toDateTimeLocalValue(new Date(startedAt)));
        }
    }, [isOpen, startedAt, isActive, showStartTimeEditor]);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            dialogRef.current?.focus();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleCenterClick = () => {
        if (isActive) {
            setShowActiveMenu(true);
        } else if (isScheduled) {
            setShowScheduledMenu(true);
        } else {
            setShowSetupMenu(true);
        }
    };

    const handleStartConfirm = async () => {
        if (isBusy) return;
        try {
            await onStart();
            setShowStartConfirm(false);
            setShowSetupMenu(false);
        } catch (error) {
            console.error('Failed to start fast', error);
        }
    };

    const handleEndFastConfirm = async () => {
        if (isBusy) return;
        try {
            await onEndFast();
            setShowActiveMenu(false);
            onOpenStatistics();
        } catch (error) {
            console.error('Failed to end fast', error);
        }
    };

    const handleSaveStartTime = async () => {
        if (isBusy) return;
        const parsed = parseDateTimeLocalValue(startTimeValue);
        if (!parsed) return;
        try {
            if (startTimeEditorMode === 'edit-active' && onUpdateStartTime) {
                await onUpdateStartTime(parsed);
            } else if (startTimeEditorMode === 'edit-scheduled' && onUpdateStartTime) {
                await onUpdateStartTime(parsed);
            } else if ((startTimeEditorMode === 'future' || startTimeEditorMode === 'past') && onSchedule) {
                await onSchedule(parsed);
                setShowSetupMenu(false);
            }
            setShowStartTimeEditor(false);
        } catch (error) {
            console.error('Failed to save start time', error);
        }
    };

    const nowLocalValue = toDateTimeLocalValue(new Date());
    const startTimeEditorTitle = startTimeEditorMode === 'edit-active' || startTimeEditorMode === 'edit-scheduled'
        ? t('fastingEditStartTime')
        : startTimeEditorMode === 'past'
            ? t('fastingSetStartTime')
            : t('fastingScheduleLater');
    const startTimeMin = startTimeEditorMode === 'future' ? nowLocalValue : undefined;
    const startTimeMax = startTimeEditorMode === 'past' || startTimeEditorMode === 'edit-active'
        ? nowLocalValue
        : undefined;

    const anyMenuOpen = showActiveMenu || showScheduledMenu || showSetupMenu || showStartTimeEditor;

    return (
        <div
            className="fixed inset-0 z-[170] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div
                ref={dialogRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="fasting-modal-title"
                className="relative w-full max-w-[340px] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-teal-100 dark:border-teal-900/60 outline-none animate-fade-in-up overflow-hidden"
                onClick={(event) => event.stopPropagation()}
            >
                {/* Main Header */}
                {!anyMenuOpen && (
                    <div className="flex items-center justify-between p-5 pb-2">
                        <div className="flex items-center gap-2">
                            <CrescentMoonIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" aria-hidden="true" />
                            <h2 id="fasting-modal-title" className="text-sm font-black uppercase tracking-widest text-teal-700 dark:text-teal-300">
                                {isActive ? t('fastingTimer') : isScheduled ? t('fastingScheduledTitle') : t('fastingStart')}
                            </h2>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={onOpenStatistics}
                                className="p-2 text-slate-400 hover:text-teal-600 dark:hover:text-teal-300 rounded-full transition-colors"
                                aria-label={t('fastingStatistics')}
                            >
                                <ChartBarIcon className="w-5 h-5" />
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full transition-colors"
                                aria-label={t('close')}
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Main Content (The Ring) */}
                {!anyMenuOpen && (
                    <div className="px-6 py-8 flex justify-center relative animate-fade-in">
                        <FastingSmartRing
                            mode={isActive ? 'active' : isScheduled ? 'scheduled' : 'setup'}
                            goalHours={goalHours}
                            remainingMs={remainingMs}
                            endTimestamp={endTimestamp}
                            language={language}
                            onGoalChange={onGoalChange}
                            onCenterClick={handleCenterClick}
                        />
                    </div>
                )}

                {/* Active Menu */}
                {showActiveMenu && (
                    <div className="animate-fade-in w-full p-6">
                        <div className="flex flex-row items-center justify-between w-full mb-6">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{t('fastingActive') || 'Active Fast'}</h3>
                            <button
                                type="button"
                                onClick={() => setShowActiveMenu(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-col gap-3 w-full">
                            <button
                                onClick={() => { setShowActiveMenu(false); openStartTimeEditor('edit-active'); }}
                                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl shadow-sm transition-colors text-sm"
                            >
                                {t('fastingEditStartTime')}
                            </button>
                            <button
                                onClick={handleEndFastConfirm}
                                className="w-full h-12 bg-transparent border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-bold rounded-xl transition-colors text-sm"
                            >
                                {t('fastingEndFast')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Scheduled Menu */}
                {showScheduledMenu && (
                    <div className="animate-fade-in w-full p-6">
                        <div className="flex flex-row items-center justify-between w-full mb-6">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{t('fastingScheduledTitle') || 'Scheduled Fast'}</h3>
                            <button
                                type="button"
                                onClick={() => setShowScheduledMenu(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-col gap-3 w-full">
                            <button
                                onClick={() => { setShowScheduledMenu(false); handleStartConfirm(); }}
                                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl shadow-sm transition-colors text-sm"
                            >
                                {t('fastingStartNow') || 'Start Now'}
                            </button>
                            <button
                                onClick={async () => {
                                    if (onCancelSchedule) {
                                        await onCancelSchedule();
                                        setShowScheduledMenu(false);
                                    }
                                }}
                                className="w-full h-12 bg-transparent border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-bold rounded-xl transition-colors text-sm"
                            >
                                {t('fastingCancelSchedule') || 'Cancel Fast'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Setup Menu */}
                {showSetupMenu && (
                    <div className="animate-fade-in w-full p-6">
                        <div className="flex flex-row items-center justify-between w-full mb-6">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{t('fastingStart') || 'Start Fast'}</h3>
                            <button
                                type="button"
                                onClick={() => setShowSetupMenu(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-col gap-3 w-full">
                            <button
                                onClick={() => { setShowSetupMenu(false); handleStartConfirm(); }}
                                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl shadow-sm transition-colors text-sm"
                            >
                                {t('fastingStartNow') || 'Start Now'}
                            </button>
                            <button
                                onClick={() => { setShowSetupMenu(false); openStartTimeEditor('past'); }}
                                className="w-full h-12 bg-transparent border border-teal-200 dark:border-teal-800/50 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 font-bold rounded-xl transition-colors text-sm"
                            >
                                {t('fastingSetStartTime')}
                            </button>
                            <button
                                onClick={() => { setShowSetupMenu(false); openStartTimeEditor('future'); }}
                                className="w-full h-12 bg-transparent border border-indigo-200 dark:border-indigo-800/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 font-bold rounded-xl transition-colors text-sm"
                            >
                                {t('fastingScheduleLater') || 'Schedule Later'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Start Time Editor */}
                {showStartTimeEditor && (
                    <div className="animate-fade-in w-full p-4">
                        <div className="flex flex-row items-center justify-between w-full mb-4">
                            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                                {startTimeEditorTitle}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowStartTimeEditor(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <FastingDateTimeField
                            value={startTimeValue}
                            onChange={setStartTimeValue}
                            language={language}
                            min={startTimeMin}
                            max={startTimeMax}
                            disabled={isBusy}
                        />
                        <div className="flex flex-col gap-2 w-full mt-4">
                            <button
                                onClick={handleSaveStartTime}
                                disabled={
                                    isBusy
                                    || !isDateTimeLocalInRange(startTimeValue, startTimeMin, startTimeMax)
                                }
                                className="w-full h-10 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl shadow-sm transition-colors disabled:opacity-50"
                            >
                                {isBusy ? '...' : startTimeEditorMode === 'past' ? t('fastingStart') : t('save')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FastingTrackerModal;
